import React, { useState, useEffect, useRef } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Box, Button, Paper, Typography, IconButton, Tooltip, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Chip, Card, CardContent, Collapse, Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, TextField, FormControl, InputLabel, Autocomplete } from '@mui/material';
import { Refresh as RefreshIcon, Print as PrintIcon, FileDownload as ExcelIcon, PictureAsPdf as PdfIcon, Delete as DeleteIcon, Add as AddIcon, Edit as EditIcon, PointOfSale as SalesIcon, ExpandMore as ExpandMoreIcon, Description as JournalIcon, CheckCircle as PostIcon, Undo as UnpostIcon, CheckCircleOutline as ApproveIcon, Warning as WarningIcon, Send as SendIcon, LayersClear as LayersClearIcon, RemoveCircleOutline as RemoveIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';
import { useFiscalDateRange } from '../hooks/useFiscalDateRange';
import axiosInstance from '../services/axiosInstance';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { companyInfoService, CompanyInfo } from '../services/companyInfo.service';
import config from '../config';
import DailySalesDialog from '../components/dailysales/DailySalesDialog';
import { DailySale } from '../types/dailySales.types';
import '../styles/print.css';

const DailySalesPage: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();
  const printRef = useRef<HTMLDivElement>(null);

  const userRoles = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return [] as string[];
      const payload = JSON.parse(atob(token.split('.')[1]));
      const roles = payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      return roles ? (Array.isArray(roles) ? roles : [roles]) : [];
    } catch { return [] as string[]; }
  })();
  const userPermissions = (() => { try { const token = localStorage.getItem('token'); if (!token) return [] as string[]; const payload = JSON.parse(atob(token.split('.')[1])); const perms = payload.Permission; return perms ? (Array.isArray(perms) ? perms : [perms]) : []; } catch { return [] as string[]; } })();
  const canPost = userPermissions.some((p: string) => p.endsWith('_POST'));
  const canCreateEntry = userPermissions.includes('DAILY_SALES_CREATE_ENTRY');
  const canDeleteEntry = userPermissions.includes('DAILY_SALES_DELETE_ENTRY');

  const [sales, setSales] = useState<DailySale[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState<DailySale | null>(null);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  
  // Filter states
  const [branches, setBranches] = useState<any[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState<number | null>(null);
  const [concepts, setConcepts] = useState<any[]>([]);
  const [selectedConceptId, setSelectedConceptId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [unsubmitDialogOpen, setUnsubmitDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [unapproveDialogOpen, setUnapproveDialogOpen] = useState(false);
  const [createEntryDialogOpen, setCreateEntryDialogOpen] = useState(false);
  const [deleteEntryDialogOpen, setDeleteEntryDialogOpen] = useState(false);
  const [postDialogOpen, setPostDialogOpen] = useState(false);
  const [unpostDialogOpen, setUnpostDialogOpen] = useState(false);
  const [selectedSaleId, setSelectedSaleId] = useState<number | null>(null);

  const t = {
    title: isRTL ? 'المبيعات اليومية' : 'Daily Sales',
    addSale: isRTL ? 'إضافة مبيعات' : 'Add Sales',
    saleNumber: isRTL ? 'رقم القيد' : 'Sale #',
    branch: isRTL ? 'الفرع' : 'Branch',
    concept: isRTL ? 'المفهوم' : 'Concept',
    date: isRTL ? 'التاريخ' : 'Date',
    grossSales: isRTL ? 'إجمالي' : 'Gross',
    discount: isRTL ? 'خصم' : 'Disc',
    serviceCharge: isRTL ? 'خدمة' : 'Service',
    netSales: isRTL ? 'صافي' : 'Net',
    delivery: isRTL ? 'ديليفري' : 'Delivery',
    dineIn: isRTL ? 'داين إن' : 'Dine-In',
    takeAway: isRTL ? 'تيك أواي' : 'Take-Away',
    status: isRTL ? 'الحالة' : 'Status',
    actions: isRTL ? 'الإجراءات' : 'Actions',
    noData: isRTL ? 'لا توجد بيانات' : 'No data available',
    refresh: isRTL ? 'تحديث' : 'Refresh',
    print: isRTL ? 'طباعة' : 'Print',
    exportExcel: isRTL ? 'تصدير Excel' : 'Export Excel',
    exportPDF: isRTL ? 'تصدير PDF' : 'Export PDF',
    edit: isRTL ? 'تعديل' : 'Edit',
    delete: isRTL ? 'حذف' : 'Delete',
    confirmDelete: isRTL ? 'هل أنت متأكد من حذف هذه المبيعات؟ لا يمكن التراجع عن هذا الإجراء!' : 'Are you sure you want to delete this sale? This action cannot be undone!',
    deleteSuccess: isRTL ? 'تم الحذف بنجاح ✓' : 'Deleted successfully ✓',
    rowsPerPage: isRTL ? 'عدد الصفوف:' : 'Rows per page:',
    draft: isRTL ? 'مسودة' : 'Draft',
    submitted: isRTL ? 'مقدم' : 'Submitted',
    approved: isRTL ? 'معتمد' : 'Approved',
    posted: isRTL ? 'مرحل' : 'Posted',
    paymentDetails: isRTL ? 'تفاصيل الدفع' : 'Payment Details',
    paymentMethod: isRTL ? 'طريقة الدفع' : 'Payment Method',
    amount: isRTL ? 'المبلغ' : 'Amount',
    submit: isRTL ? 'تقديم للاعتماد' : 'Submit for Approval',
    unsubmit: isRTL ? 'إلغاء التقديم' : 'Cancel Submit',
    approve: isRTL ? 'اعتماد المبيعات' : 'Approve Sale',
    unapprove: isRTL ? 'إلغاء الاعتماد' : 'Cancel Approval',
    createEntry: isRTL ? 'إنشاء قيد محاسبي' : 'Create Journal Entry',
    deleteEntry: isRTL ? 'حذف القيد' : 'Delete Entry',
    post: isRTL ? 'ترحيل القيد' : 'Post Entry',
    unpost: isRTL ? 'إلغاء الترحيل' : 'Unpost Entry',
    confirmSubmit: isRTL ? 'سيتم تقديم المبيعات للاعتماد من قبل المدير' : 'This will submit the sale for manager approval',
    confirmUnsubmit: isRTL ? 'سيتم إلغاء التقديم وإعادة المبيعات لحالة المسودة' : 'This will cancel submission and return sale to draft status',
    confirmApprove: isRTL ? 'سيتم اعتماد المبيعات وإتاحتها لإنشاء القيد المحاسبي' : 'This will approve the sale and make it available for journal entry creation',
    confirmUnapprove: isRTL ? 'سيتم إلغاء الاعتماد وإعادة المبيعات لحالة التقديم' : 'This will cancel approval and return sale to submitted status',
    confirmCreateEntry: isRTL ? 'سيتم إنشاء قيد محاسبي تلقائي لهذه المبيعات' : 'This will automatically create a journal entry for this sale',
    confirmDeleteEntry: isRTL ? 'سيتم حذف القيد المحاسبي نهائياً - لا يمكن التراجع!' : 'This will permanently delete the journal entry - cannot be undone!',
    confirmPost: isRTL ? 'سيتم ترحيل القيد إلى دفتر الأستاذ العام ولا يمكن التعديل بعد ذلك' : 'This will post the entry to General Ledger and cannot be modified after',
    confirmUnpost: isRTL ? 'سيتم حذف القيد من دفتر الأستاذ وإعادته لحالة المسودة' : 'This will remove the entry from General Ledger and return it to draft status',
    submitSuccess: isRTL ? 'تم التقديم للاعتماد بنجاح ✓' : 'Submitted for approval successfully ✓',
    unsubmitSuccess: isRTL ? 'تم إلغاء التقديم بنجاح ✓' : 'Submission cancelled successfully ✓',
    approveSuccess: isRTL ? 'تم اعتماد المبيعات بنجاح ✓' : 'Sale approved successfully ✓',
    unapproveSuccess: isRTL ? 'تم إلغاء الاعتماد بنجاح ✓' : 'Approval cancelled successfully ✓',
    entryCreated: isRTL ? 'تم إنشاء القيد المحاسبي بنجاح ✓' : 'Journal entry created successfully ✓',
    entryDeleted: isRTL ? 'تم حذف القيد بنجاح ✓' : 'Entry deleted successfully ✓',
    entryPosted: isRTL ? 'تم الترحيل بنجاح ✓' : 'Posted successfully ✓',
    entryUnposted: isRTL ? 'تم إلغاء الترحيل بنجاح ✓' : 'Unposted successfully ✓',
    cancel: isRTL ? 'إلغاء' : 'Cancel',
    confirm: isRTL ? 'تأكيد' : 'Confirm',
    filterByBranch: isRTL ? 'فلترة حسب الفرع' : 'Filter by Branch',
    allBranches: isRTL ? 'جميع الفروع' : 'All Branches',
    filterByConcept: isRTL ? 'الكونسبت' : 'Concept',
    allConcepts: isRTL ? 'جميع الكونسبتات' : 'All Concepts',
    startDate: isRTL ? 'من تاريخ' : 'Start Date',
    endDate: isRTL ? 'إلى تاريخ' : 'End Date',
    applyFilters: isRTL ? 'تطبيق الفلاتر' : 'Apply Filters',
    clearFilters: isRTL ? 'مسح الفلاتر' : 'Clear Filters',
    totalRecords: isRTL ? 'إجمالي السجلات' : 'Total Records',
    totalNet: isRTL ? 'إجمالي الصافي' : 'Total Net'
  };

  useEffect(() => {
    loadCompanyInfo();
    loadBranches();
    loadConcepts();
    // Set default date range: last 30 days
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
    setEndDate(today.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    loadConcepts();
    setSelectedConceptId(null);
  }, [selectedBranchId]);

  useEffect(() => {
    if (startDate && endDate) {
      loadSales();
    }
  }, [startDate, endDate, selectedBranchId]);

  const loadCompanyInfo = async () => {
    try { const info = await companyInfoService.getCompanyInfo(); setCompanyInfo(info); } 
    catch (error) { console.error('Failed to load company info:', error); }
  };

  const loadBranches = async () => {
    try {
      const response = await axiosInstance.get('/Branches');
      setBranches(response.data);
    } catch (error: any) {
      console.error('Failed to load branches:', error);
    }
  };

  const loadConcepts = async () => {
    try {
      const url = selectedBranchId ? `/Concepts/branch/${selectedBranchId}` : '/Concepts';
      const response = await axiosInstance.get(url);
      setConcepts(response.data);
    } catch (error: any) {
      console.error('Failed to load concepts:', error);
    }
  };

  const loadSales = async () => {
    if (!startDate || !endDate) return;
    
    try {
      setLoading(true);
      let url = `/DailySales/date-range?startDate=${startDate}&endDate=${endDate}`;
      if (selectedBranchId) {
        url += `&branchId=${selectedBranchId}`;
      }
      const response = await axiosInstance.get(url);
      setSales(response.data);
    } catch (error: any) {
      toast.error(isRTL ? 'فشل تحميل المبيعات' : 'Failed to load sales');
    } finally { setLoading(false); }
  };

  const handleApplyFilters = () => {
    setPage(0);
    loadSales();
  };

  const handleClearFilters = () => {
    setSelectedBranchId(null);
    setSelectedConceptId(null);
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
    setEndDate(today.toISOString().split('T')[0]);
    setPage(0);
  };

  const handleAdd = () => { setSelectedSale(null); setDialogOpen(true); };
  const handleEdit = (sale: DailySale) => { setSelectedSale(sale); setDialogOpen(true); };
  const handleDialogClose = (refresh?: boolean) => { setDialogOpen(false); setSelectedSale(null); if (refresh) loadSales(); };

  const openDeleteDialog = (id: number) => { setSelectedSaleId(id); setDeleteDialogOpen(true); };
  const openSubmitDialog = (id: number) => { setSelectedSaleId(id); setSubmitDialogOpen(true); };
  const openUnsubmitDialog = (id: number) => { setSelectedSaleId(id); setUnsubmitDialogOpen(true); };
  const openApproveDialog = (id: number) => { setSelectedSaleId(id); setApproveDialogOpen(true); };
  const openUnapproveDialog = (id: number) => { setSelectedSaleId(id); setUnapproveDialogOpen(true); };
  const openCreateEntryDialog = (id: number) => { setSelectedSaleId(id); setCreateEntryDialogOpen(true); };
  const openDeleteEntryDialog = (id: number) => { setSelectedSaleId(id); setDeleteEntryDialogOpen(true); };
  const openPostDialog = (id: number) => { setSelectedSaleId(id); setPostDialogOpen(true); };
  const openUnpostDialog = (id: number) => { setSelectedSaleId(id); setUnpostDialogOpen(true); };

  const handleDelete = async () => {
    if (!selectedSaleId) return;
    try {
      await axiosInstance.delete(`/DailySales/${selectedSaleId}`);
      toast.success(t.deleteSuccess);
      loadSales();
      setDeleteDialogOpen(false);
      setSelectedSaleId(null);
    } catch (error: any) {
      toast.error(error.response?.data?.message || (isRTL ? 'فشل الحذف' : 'Failed to delete'));
    }
  };

  const handleSubmit = async () => {
    if (!selectedSaleId) return;
    try {
      await axiosInstance.post(`/DailySales/${selectedSaleId}/submit`, { 
        DailySaleId: selectedSaleId
      });
      toast.success(t.submitSuccess);
      loadSales();
      setSubmitDialogOpen(false);
      setSelectedSaleId(null);
    } catch (error: any) {
      toast.error(error.response?.data?.message || (isRTL ? 'فشل التقديم' : 'Failed to submit'));
    }
  };

  const handleUnsubmit = async () => {
    if (!selectedSaleId) return;
    try {
      await axiosInstance.post(`/DailySales/${selectedSaleId}/unsubmit`);
      toast.success(t.unsubmitSuccess);
      loadSales();
      setUnsubmitDialogOpen(false);
      setSelectedSaleId(null);
    } catch (error: any) {
      toast.error(error.response?.data?.message || (isRTL ? 'فشل إلغاء التقديم' : 'Failed to cancel submit'));
    }
  };

  const handleApprove = async () => {
    if (!selectedSaleId) return;
    try {
      await axiosInstance.post(`/DailySales/${selectedSaleId}/approve`, { 
        DailySaleId: selectedSaleId
      });
      toast.success(t.approveSuccess);
      loadSales();
      setApproveDialogOpen(false);
      setSelectedSaleId(null);
    } catch (error: any) {
      toast.error(error.response?.data?.message || (isRTL ? 'فشل الاعتماد' : 'Failed to approve'));
    }
  };

  const handleUnapprove = async () => {
    if (!selectedSaleId) return;
    try {
      await axiosInstance.post(`/DailySales/${selectedSaleId}/unapprove`);
      toast.success(t.unapproveSuccess);
      loadSales();
      setUnapproveDialogOpen(false);
      setSelectedSaleId(null);
    } catch (error: any) {
      toast.error(error.response?.data?.message || (isRTL ? 'فشل إلغاء الاعتماد' : 'Failed to cancel approval'));
    }
  };

  const handleCreateEntry = async () => {
    if (!selectedSaleId) return;
    try {
      await axiosInstance.post(`/DailySales/${selectedSaleId}/create-entry`);
      toast.success(t.entryCreated);
      loadSales();
      setCreateEntryDialogOpen(false);
      setSelectedSaleId(null);
    } catch (error: any) {
      toast.error(error.response?.data?.message || (isRTL ? 'فشل إنشاء القيد' : 'Failed to create entry'));
    }
  };

  const handleDeleteEntry = async () => {
    if (!selectedSaleId) return;
    try {
      await axiosInstance.post(`/DailySales/${selectedSaleId}/delete-entry`);
      toast.success(t.entryDeleted);
      loadSales();
      setDeleteEntryDialogOpen(false);
      setSelectedSaleId(null);
    } catch (error: any) {
      toast.error(error.response?.data?.message || (isRTL ? 'فشل حذف القيد' : 'Failed to delete entry'));
    }
  };

  const handlePost = async () => {
    if (!selectedSaleId) return;
    try {
      await axiosInstance.post(`/DailySales/${selectedSaleId}/post`);
      toast.success(t.entryPosted);
      loadSales();
      setPostDialogOpen(false);
      setSelectedSaleId(null);
    } catch (error: any) {
      toast.error(error.response?.data?.message || (isRTL ? 'فشل الترحيل' : 'Failed to post'));
    }
  };

  const handleUnpost = async () => {
    if (!selectedSaleId) return;
    try {
      await axiosInstance.post(`/DailySales/${selectedSaleId}/unpost`);
      toast.success(t.entryUnposted);
      loadSales();
      setUnpostDialogOpen(false);
      setSelectedSaleId(null);
    } catch (error: any) {
      toast.error(error.response?.data?.message || (isRTL ? 'فشل إلغاء الترحيل' : 'Failed to unpost'));
    }
  };

  const getStatusChip = (sale: DailySale) => {
    if (sale.isPosted) return <Chip label={t.posted} color="success" size="small" />;
    if (sale.isApproved) return <Chip label={t.approved} color="info" size="small" />;
    if (sale.status === 'Submitted') return <Chip label={t.submitted} color="warning" size="small" />;
    return <Chip label={t.draft} color="default" size="small" />;
  };

  const handleChangePage = (_: unknown, newPage: number) => { setPage(newPage); };
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };

  const getAllPaymentMethods = () => {
    const methods = new Set<string>();
    filteredSales.forEach(sale => {
      sale.paymentDetails?.forEach(detail => {
        methods.add(isRTL ? (detail.methodNameAr || detail.methodName || '') : (detail.methodName || ''));
      });
    });
    return Array.from(methods).sort();
  };

  const exportToExcel = async () => {
    if (filteredSales.length === 0) { toast.error(isRTL ? 'لا توجد بيانات للتصدير' : 'No data to export'); return; }
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(isRTL ? 'المبيعات' : 'Sales');
      const exportData = filteredSales;
      const payMethods = getAllPaymentMethods();
      let currentRow = 1;
      if (companyInfo?.logoBase64) {
        try { const img = workbook.addImage({ base64: companyInfo.logoBase64, extension: 'png' }); worksheet.addImage(img, { tl: { col: 0, row: 0 }, ext: { width: 80, height: 60 } }); } catch {}
      }
      const lastCol = String.fromCharCode(65 + 7 + payMethods.length);
      worksheet.mergeCells(`B1:${lastCol}1`); const compCell = worksheet.getCell('B1'); compCell.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName; compCell.font = { size: 18, bold: true, color: { argb: 'FFFF6F00' } }; compCell.alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.mergeCells(`B2:${lastCol}2`); const adrCell = worksheet.getCell('B2'); adrCell.value = `📍 ${companyInfo?.address || ''}`; adrCell.font = { size: 9, color: { argb: 'FF424242' } }; adrCell.alignment = { horizontal: 'center' };
      worksheet.mergeCells(`B3:${lastCol}3`); const conCell = worksheet.getCell('B3'); conCell.value = `📞 ${companyInfo?.phone || ''} | ✉️ ${companyInfo?.email || ''}`; conCell.font = { size: 9, color: { argb: 'FF424242' } }; conCell.alignment = { horizontal: 'center' };
      currentRow = 5;
      worksheet.mergeCells(`A${currentRow}:${lastCol}${currentRow}`); const ttlCell = worksheet.getCell(`A${currentRow}`); ttlCell.value = isRTL ? 'تقرير المبيعات اليومية' : 'Daily Sales Report'; ttlCell.font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } }; ttlCell.alignment = { horizontal: 'center', vertical: 'middle' }; ttlCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF6F00' } }; worksheet.getRow(currentRow).height = 25; currentRow++;
      worksheet.mergeCells(`A${currentRow}:${lastCol}${currentRow}`); const dtCell = worksheet.getCell(`A${currentRow}`); dtCell.value = `${isRTL ? 'التاريخ:' : 'Date:'} ${new Date().toLocaleDateString(isRTL ? 'ar-EG' : 'en-GB')}`; dtCell.font = { size: 10, bold: true, color: { argb: 'FFFF6F00' } }; dtCell.alignment = { horizontal: 'center' }; currentRow += 2;
      const sumRow = currentRow; const midCol = String.fromCharCode(65 + Math.floor((7 + payMethods.length) / 2));
      worksheet.mergeCells(`A${sumRow}:${midCol}${sumRow}`); const cntCell = worksheet.getCell(`A${sumRow}`); cntCell.value = `${isRTL ? 'إجمالي القيود:' : 'Total:'} ${filteredSales.length}`; cntCell.font = { size: 10, bold: true, color: { argb: 'FFFFFFFF' } }; cntCell.alignment = { horizontal: 'center', vertical: 'middle' }; cntCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4CAF50' } }; worksheet.getRow(sumRow).height = 25;
      const nextCol = String.fromCharCode(midCol.charCodeAt(0) + 1);
      worksheet.mergeCells(`${nextCol}${sumRow}:${lastCol}${sumRow}`); const netCell = worksheet.getCell(`${nextCol}${sumRow}`); const totNet = filteredSales.reduce((s, x) => s + (x.netSales || 0), 0); netCell.value = `${isRTL ? 'صافي المبيعات:' : 'Net Sales:'} ${totNet.toFixed(3)} KD`; netCell.font = { size: 10, bold: true, color: { argb: 'FFFFFFFF' } }; netCell.alignment = { horizontal: 'center', vertical: 'middle' }; netCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF6F00' } }; currentRow += 2;
      const hdrRow = worksheet.getRow(currentRow);
      const headers = [isRTL ? 'القيد' : 'Sale', isRTL ? 'الفرع' : 'Branch', isRTL ? 'المفهوم' : 'Concept', isRTL ? 'التاريخ' : 'Date', isRTL ? 'إجمالي' : 'Gross', isRTL ? 'خصم' : 'Disc', isRTL ? 'خدمة' : 'Service', isRTL ? 'صافي' : 'Net', isRTL ? 'ديليفري' : 'Delivery', isRTL ? 'داين إن' : 'Dine-In', isRTL ? 'تيك أواي' : 'Take-Away', ...payMethods.map(m => m.length > 8 ? m.substring(0, 8) : m), isRTL ? 'الحالة' : 'Status'];
      hdrRow.values = headers;
      hdrRow.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 9 }; hdrRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF6F00' } }; hdrRow.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }; hdrRow.height = 30;
      for (let i = 1; i <= headers.length; i++) { hdrRow.getCell(i).border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } }; }
      currentRow++;
      filteredSales.forEach((sale, idx) => {
        const row = worksheet.getRow(currentRow);
        const paymentMap: {[key: string]: number} = {};
        sale.paymentDetails?.forEach(p => {
          const methodName = isRTL ? (p.methodNameAr || p.methodName || '') : (p.methodName || '');
          paymentMap[methodName] = (p.amount || 0);
        });
        const rowData: any[] = [
          sale.saleNumber,
          isRTL ? sale.branchNameAr : sale.branchName,
          isRTL ? sale.conceptNameAr : sale.conceptName,
          new Date(sale.saleDate).toLocaleDateString(isRTL ? 'ar-EG' : 'en-GB'),
          (sale.grossSales || 0).toFixed(3),
          (sale.discount || 0).toFixed(3),
          (sale.serviceCharge || 0).toFixed(3),
          (sale.netSales || 0).toFixed(3),
          (sale.deliveryAmount || 0).toFixed(3),
          (sale.dineInAmount || 0).toFixed(3),
          (sale.takeAwayAmount || 0).toFixed(3)
        ];
        payMethods.forEach(method => {
          rowData.push(paymentMap[method] ? paymentMap[method].toFixed(3) : '0.000');
        });
        rowData.push(sale.isPosted ? t.posted : (sale.isApproved ? t.approved : (sale.status === 'Submitted' ? t.submitted : t.draft)));
        row.values = rowData;
        row.alignment = { horizontal: 'center', vertical: 'middle' }; row.height = 20;
        for (let i = 1; i <= rowData.length; i++) {
          const cell = row.getCell(i);
          cell.border = { top: { style: 'thin', color: { argb: 'FFD3D3D3' } }, bottom: { style: 'thin', color: { argb: 'FFD3D3D3' } }, left: { style: 'thin', color: { argb: 'FFD3D3D3' } }, right: { style: 'thin', color: { argb: 'FFD3D3D3' } } };
          if (idx % 2 === 0) { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } }; }
        }
        currentRow++;
      });
      const colWidths = [15, 18, 18, 11, 11, 10, 10, 11, 11, 11, 11];
      payMethods.forEach(() => colWidths.push(12));
      colWidths.push(10);
      worksheet.columns = colWidths.map(w => ({ width: w }));
      const buf = await workbook.xlsx.writeBuffer(); const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }); const url = URL.createObjectURL(blob); const lnk = document.createElement('a'); lnk.href = url; lnk.download = `DailySales_${new Date().toISOString().split('T')[0]}.xlsx`; lnk.click(); URL.revokeObjectURL(url); toast.success(t.exportExcel + ' ✓');
    } catch { toast.error(isRTL ? 'فشل التصدير' : 'Export failed'); }
  };

  const exportToPdf = async () => {
    if (!companyInfo || filteredSales.length === 0) { toast.error(isRTL ? 'لا توجد بيانات' : 'No data'); return; }
    try {
      toast.info(isRTL ? 'جاري إنشاء PDF...' : 'Generating PDF...');
      const payMethods = getAllPaymentMethods();
      const totNet = filteredSales.reduce((s, x) => s + (x.netSales || 0), 0);
      const div = document.createElement('div');
      div.style.position = 'absolute'; div.style.left = '-9999px'; div.style.width = '297mm'; div.style.background = 'white'; div.style.padding = '10mm'; div.style.fontFamily = 'Arial, sans-serif'; div.style.direction = isRTL ? 'rtl' : 'ltr';
      const paymentHeaders = payMethods.map(m => `<th style="padding: 5px 2px; text-align: right; border: 1px solid #FF6F00; font-size: 6px;">${m.length > 7 ? m.substring(0, 7) : m}</th>`).join('');
      const salesRows = filteredSales.map((s, i) => {
        const paymentMap: {[key: string]: number} = {};
        s.paymentDetails?.forEach(p => {
          const methodName = isRTL ? (p.methodNameAr || p.methodName || '') : (p.methodName || '');
          paymentMap[methodName] = (p.amount || 0);
        });
        const paymentCells = payMethods.map(method => `<td style="padding: 4px 3px; border: 1px solid #E0E0E0; text-align: right; color: #000; font-size: 6px;">${paymentMap[method] ? paymentMap[method].toFixed(3) : '0.000'}</td>`).join('');
        return `<tr style="background: ${i % 2 === 0 ? '#FFF' : '#F5F5F5'};"><td style="padding: 4px 3px; border: 1px solid #E0E0E0; text-align: ${isRTL ? 'right' : 'left'}; color: #000; font-size: 7px;">${s.saleNumber}</td><td style="padding: 4px 3px; border: 1px solid #E0E0E0; text-align: ${isRTL ? 'right' : 'left'}; color: #000; font-size: 7px;">${isRTL ? s.branchNameAr : s.branchName}</td><td style="padding: 4px 3px; border: 1px solid #E0E0E0; text-align: ${isRTL ? 'right' : 'left'}; color: #000; font-size: 7px;">${isRTL ? s.conceptNameAr : s.conceptName}</td><td style="padding: 4px 3px; border: 1px solid #E0E0E0; text-align: center; color: #000; font-size: 7px;">${new Date(s.saleDate).toLocaleDateString(isRTL ? 'ar-EG' : 'en-GB')}</td><td style="padding: 4px 3px; border: 1px solid #E0E0E0; text-align: right; color: #000; font-size: 6px;">${(s.grossSales || 0).toFixed(3)}</td><td style="padding: 4px 3px; border: 1px solid #E0E0E0; text-align: right; color: #000; font-size: 6px;">${(s.discount || 0).toFixed(3)}</td><td style="padding: 4px 3px; border: 1px solid #E0E0E0; text-align: right; color: #000; font-size: 6px;">${(s.serviceCharge || 0).toFixed(3)}</td><td style="padding: 4px 3px; border: 1px solid #E0E0E0; text-align: right; color: #000; font-weight: bold; font-size: 6px;">${(s.netSales || 0).toFixed(3)}</td><td style="padding: 4px 3px; border: 1px solid #E0E0E0; text-align: right; color: #000; font-size: 6px;">${(s.deliveryAmount || 0).toFixed(3)}</td><td style="padding: 4px 3px; border: 1px solid #E0E0E0; text-align: right; color: #000; font-size: 6px;">${(s.dineInAmount || 0).toFixed(3)}</td><td style="padding: 4px 3px; border: 1px solid #E0E0E0; text-align: right; color: #000; font-size: 6px;">${(s.takeAwayAmount || 0).toFixed(3)}</td>${paymentCells}</tr>`;
      }).join('');
      div.innerHTML = `<div style="text-align: center; border-bottom: 3px solid #FF6F00; padding-bottom: 8px; margin-bottom: 10px;">${companyInfo.logoBase64 ? `<img src="${companyInfo.logoBase64}" style="width: 70px; height: 50px; margin-bottom: 6px;">` : ''}<div style="font-size: 20px; font-weight: bold; color: #FF6F00;">${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</div><div style="font-size: 9px; color: #666; margin-top: 2px;">${companyInfo.address || ''}</div><div style="font-size: 9px; color: #666;">${companyInfo.phone || ''} | ${companyInfo.email || ''}</div></div><div style="background: #FF6F00; color: white; padding: 6px; text-align: center; font-size: 16px; font-weight: bold; margin: 10px 0;">${isRTL ? 'تقرير المبيعات اليومية' : 'Daily Sales Report'}</div><div style="text-align: center; margin-bottom: 10px; font-size: 9px; color: #666;">${isRTL ? 'التاريخ:' : 'Date:'} ${new Date().toLocaleDateString(isRTL ? 'ar-EG' : 'en-GB')}</div><div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; margin: 10px 0;"><div style="border: 2px solid #4CAF50; padding: 8px; text-align: center; border-radius: 5px; background: #E8F5E9;"><div style="font-size: 9px; color: #2E7D32; margin-bottom: 4px;">${isRTL ? 'إجمالي القيود' : 'Total Records'}</div><div style="font-size: 18px; font-weight: bold; color: #2E7D32;">${filteredSales.length}</div></div><div style="border: 2px solid #FF6F00; padding: 8px; text-align: center; border-radius: 5px; background: #FFF3E0;"><div style="font-size: 9px; color: #E65100; margin-bottom: 4px;">${isRTL ? 'صافي المبيعات' : 'Net Sales'}</div><div style="font-size: 18px; font-weight: bold; color: #E65100;">${totNet.toFixed(3)} KD</div></div></div><table style="width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 6px;"><thead><tr style="background: #FF6F00; color: white;"><th style="padding: 5px 2px; text-align: ${isRTL ? 'right' : 'left'}; border: 1px solid #FF6F00; font-size: 6px;">${isRTL ? 'القيد' : 'Sale'}</th><th style="padding: 5px 2px; text-align: ${isRTL ? 'right' : 'left'}; border: 1px solid #FF6F00; font-size: 6px;">${isRTL ? 'الفرع' : 'Branch'}</th><th style="padding: 5px 2px; text-align: ${isRTL ? 'right' : 'left'}; border: 1px solid #FF6F00; font-size: 6px;">${isRTL ? 'المفهوم' : 'Concept'}</th><th style="padding: 5px 2px; text-align: center; border: 1px solid #FF6F00; font-size: 6px;">${isRTL ? 'التاريخ' : 'Date'}</th><th style="padding: 5px 2px; text-align: right; border: 1px solid #FF6F00; font-size: 6px;">${isRTL ? 'إجمالي' : 'Gross'}</th><th style="padding: 5px 2px; text-align: right; border: 1px solid #FF6F00; font-size: 6px;">${isRTL ? 'خصم' : 'Disc'}</th><th style="padding: 5px 2px; text-align: right; border: 1px solid #FF6F00; font-size: 6px;">${isRTL ? 'خدمة' : 'Service'}</th><th style="padding: 5px 2px; text-align: right; border: 1px solid #FF6F00; font-size: 6px;">${isRTL ? 'صافي' : 'Net'}</th><th style="padding: 5px 2px; text-align: right; border: 1px solid #FF6F00; font-size: 6px;">${isRTL ? 'ديليفري' : 'Delivery'}</th><th style="padding: 5px 2px; text-align: right; border: 1px solid #FF6F00; font-size: 6px;">${isRTL ? 'داين إن' : 'Dine-In'}</th><th style="padding: 5px 2px; text-align: right; border: 1px solid #FF6F00; font-size: 6px;">${isRTL ? 'تيك أواي' : 'Take-Away'}</th>${paymentHeaders}</tr></thead><tbody>${salesRows}</tbody></table>`;
      document.body.appendChild(div); const cnv = await html2canvas(div, { scale: 2, useCORS: true, logging: false, backgroundColor: '#fff' }); const imgD = cnv.toDataURL('image/png'); const pdf = new jsPDF('l', 'mm', 'a4'); const pW = pdf.internal.pageSize.getWidth(); const pH = pdf.internal.pageSize.getHeight(); const iW = pW; const iH = (cnv.height * iW) / cnv.width; let hL = iH; let pos = 0; pdf.addImage(imgD, 'PNG', 0, pos, iW, iH); hL -= pH;
      while (hL > 0) { pos = hL - iH; pdf.addPage(); pdf.addImage(imgD, 'PNG', 0, pos, iW, iH); hL -= pH; }
      pdf.save(`DailySales_${new Date().toISOString().split('T')[0]}.pdf`); document.body.removeChild(div); toast.success(t.exportPDF + ' ✓');
    } catch { toast.error(isRTL ? 'فشل التصدير' : 'Export failed'); }
  };

  const handlePrintSingle = (sale: DailySale) => {
    const payMethodsList = getAllPaymentMethods();
    const paymentMap: {[key: string]: number} = {};
    sale.paymentDetails?.forEach(p => {
      const methodName = isRTL ? (p.methodNameAr || p.methodName || '') : (p.methodName || '');
      paymentMap[methodName] = (p.amount || 0);
    });
    const statusText = sale.isPosted ? (isRTL ? 'مرحّل' : 'Posted') : sale.isApproved ? (isRTL ? 'معتمد' : 'Approved') : sale.status === 'Submitted' ? (isRTL ? 'مقدّم' : 'Submitted') : (isRTL ? 'مسودة' : 'Draft');
    const html = `<!DOCTYPE html><html dir="${isRTL ? 'rtl' : 'ltr'}"><head><style>
      body{font-family:${isRTL ? 'Cairo' : 'Arial'},sans-serif;margin:20px;font-size:12px;color:#333;}
      .header{text-align:center;border-bottom:3px solid #FF6F00;padding-bottom:10px;margin-bottom:15px;}
      .header h2{color:#FF6F00;margin:5px 0;} .header p{font-size:11px;color:#666;margin:0;}
      h1{text-align:center;color:#FF6F00;margin:10px 0 20px;font-size:20px;}
      .info-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin-bottom:20px;border:2px solid #FF6F00;border-radius:8px;padding:15px;}
      .info-item label{font-size:10px;color:#999;display:block;} .info-item span{font-weight:bold;font-size:14px;}
      table{width:100%;border-collapse:collapse;margin-top:10px;}
      th,td{border:1px solid #ddd;padding:8px;font-size:11px;}
      th{background:#FF6F00;color:white;font-weight:bold;}
      .total-row{background:#FFF3E0;font-weight:bold;}
      .signatures{display:flex;justify-content:space-between;margin-top:60px;}
      .sig-box{text-align:center;width:30%;} .sig-line{border-top:1px solid #000;padding-top:5px;margin-top:60px;}
      @page{size:A4;margin:1cm;}
    </style></head><body>
      <div class="header">
        ${companyInfo?.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" style="max-height:50px;margin-bottom:5px;" onerror="this.style.display='none'" />` : ''}
        <h2>${isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName}</h2>
        <p>${companyInfo?.address ? companyInfo.address + ' | ' : ''}${companyInfo?.phone ? companyInfo.phone + ' | ' : ''}${companyInfo?.email || ''}</p>
      </div>
      <h1>${isRTL ? 'تقرير مبيعات يومية' : 'Daily Sales Report'}</h1>
      <div class="info-grid">
        <div class="info-item"><label>${t.saleNumber}</label><span style="color:#FF6F00">${sale.saleNumber}</span></div>
        <div class="info-item"><label>${t.date}</label><span>${new Date(sale.saleDate).toLocaleDateString(isRTL ? 'ar-EG' : 'en-GB')}</span></div>
        <div class="info-item"><label>${t.status}</label><span>${statusText}</span></div>
        <div class="info-item"><label>${t.branch}</label><span>${isRTL ? sale.branchNameAr : sale.branchName}</span></div>
        <div class="info-item"><label>${t.concept}</label><span>${isRTL ? sale.conceptNameAr : sale.conceptName}</span></div>
        ${sale.journalEntryNumber ? `<div class="info-item"><label>${isRTL ? 'رقم القيد' : 'Journal Entry'}</label><span style="color:#2e7d32">${sale.journalEntryNumber} (#${sale.journalEntryId})</span></div>` : ''}
      </div>
      <table>
        <thead><tr>
          <th>${t.grossSales}</th><th>${t.discount}</th><th>${t.serviceCharge}</th><th>${t.netSales}</th>
          <th>${t.delivery}</th><th>${t.dineIn}</th><th>${t.takeAway}</th>
        </tr></thead>
        <tbody><tr>
          <td style="text-align:right">${(sale.grossSales || 0).toFixed(3)}</td>
          <td style="text-align:right">${(sale.discount || 0).toFixed(3)}</td>
          <td style="text-align:right">${(sale.serviceCharge || 0).toFixed(3)}</td>
          <td style="text-align:right;font-weight:bold;color:#FF6F00">${(sale.netSales || 0).toFixed(3)}</td>
          <td style="text-align:right">${(sale.deliveryAmount || 0).toFixed(3)}</td>
          <td style="text-align:right">${(sale.dineInAmount || 0).toFixed(3)}</td>
          <td style="text-align:right">${(sale.takeAwayAmount || 0).toFixed(3)}</td>
        </tr></tbody>
      </table>
      ${payMethodsList.length > 0 ? `
        <h3 style="color:#FF6F00;margin-top:20px">${isRTL ? 'طرق الدفع' : 'Payment Methods'}</h3>
        <table><thead><tr>${payMethodsList.map(m => `<th>${m}</th>`).join('')}</tr></thead>
        <tbody><tr>${payMethodsList.map(m => `<td style="text-align:right;font-weight:bold">${paymentMap[m] ? paymentMap[m].toFixed(3) : '0.000'}</td>`).join('')}</tr></tbody></table>
      ` : ''}
      ${sale.notes ? `<p style="margin-top:15px"><strong>${isRTL ? 'ملاحظات:' : 'Notes:'}</strong> ${sale.notes}</p>` : ''}
      <div class="signatures">
        <div class="sig-box"><div class="sig-line">${isRTL ? 'مدير الفرع' : 'Branch Manager'}</div></div>
        <div class="sig-box"><div class="sig-line">${isRTL ? 'المحاسب' : 'Accountant'}</div></div>
        <div class="sig-box"><div class="sig-line">${isRTL ? 'المدير المالي' : 'Financial Manager'}</div></div>
      </div>
    </body></html>`;
    const w = window.open('', '_blank', 'width=900,height=700');
    if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 500); }
  };

  const handlePrint = () => {
    const payMethodsList = getAllPaymentMethods();
    const rows = filteredSales.map((sale) => {
      const paymentMap: {[key: string]: number} = {};
      sale.paymentDetails?.forEach(p => {
        const methodName = isRTL ? (p.methodNameAr || p.methodName || '') : (p.methodName || '');
        paymentMap[methodName] = (p.amount || 0);
      });
      const statusText = sale.isPosted ? (isRTL ? 'مرحّل' : 'Posted') : sale.isApproved ? (isRTL ? 'معتمد' : 'Approved') : sale.status === 'Submitted' ? (isRTL ? 'مقدّم' : 'Submitted') : (isRTL ? 'مسودة' : 'Draft');
      const statusColor = sale.isPosted ? '#2e7d32' : sale.isApproved ? '#9c27b0' : sale.status === 'Submitted' ? '#ed6c02' : '#9e9e9e';
      return `<tr>
        <td>${sale.saleNumber}</td>
        <td>${isRTL ? sale.branchNameAr : sale.branchName}</td>
        <td>${isRTL ? sale.conceptNameAr : sale.conceptName}</td>
        <td>${new Date(sale.saleDate).toLocaleDateString(isRTL ? 'ar-EG' : 'en-GB')}</td>
        <td style="text-align:right">${(sale.grossSales || 0).toFixed(3)}</td>
        <td style="text-align:right">${(sale.discount || 0).toFixed(3)}</td>
        <td style="text-align:right">${(sale.serviceCharge || 0).toFixed(3)}</td>
        <td style="text-align:right;font-weight:bold">${(sale.netSales || 0).toFixed(3)}</td>
        <td style="text-align:right">${(sale.deliveryAmount || 0).toFixed(3)}</td>
        <td style="text-align:right">${(sale.dineInAmount || 0).toFixed(3)}</td>
        <td style="text-align:right">${(sale.takeAwayAmount || 0).toFixed(3)}</td>
        ${payMethodsList.map(m => `<td style="text-align:right">${paymentMap[m] ? paymentMap[m].toFixed(3) : '0.000'}</td>`).join('')}
        <td style="text-align:center"><span style="padding:2px 8px;border-radius:10px;font-size:10px;color:white;background:${statusColor}">${statusText}</span></td>
      </tr>`;
    }).join('');
    const totals = {
      gross: filteredSales.reduce((s, d) => s + (d.grossSales || 0), 0),
      discount: filteredSales.reduce((s, d) => s + (d.discount || 0), 0),
      service: filteredSales.reduce((s, d) => s + (d.serviceCharge || 0), 0),
      net: filteredSales.reduce((s, d) => s + (d.netSales || 0), 0),
      delivery: filteredSales.reduce((s, d) => s + (d.deliveryAmount || 0), 0),
      dineIn: filteredSales.reduce((s, d) => s + (d.dineInAmount || 0), 0),
      takeAway: filteredSales.reduce((s, d) => s + (d.takeAwayAmount || 0), 0),
    };
    const html = `<!DOCTYPE html><html dir="${isRTL ? 'rtl' : 'ltr'}"><head><style>
      body{font-family:${isRTL ? 'Cairo' : 'Arial'},sans-serif;margin:20px;font-size:11px;color:#333;}
      .header{text-align:center;border-bottom:3px solid #FF6F00;padding-bottom:10px;margin-bottom:15px;}
      .header h2{color:#FF6F00;margin:5px 0;} .header p{font-size:10px;color:#666;margin:0;}
      h1{text-align:center;color:#FF6F00;margin:10px 0 15px;font-size:18px;}
      table{width:100%;border-collapse:collapse;margin-top:10px;}
      th,td{border:1px solid #ddd;padding:4px 6px;font-size:9px;}
      th{background:#FF6F00;color:white;font-weight:bold;}
      tr:nth-child(even){background:#f9f9f9;}
      .total-row{background:#FFF3E0;font-weight:bold;}
      @page{size:A4 landscape;margin:8mm;}
    </style></head><body>
      <div class="header">
        ${companyInfo?.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" style="max-height:50px;margin-bottom:5px;" onerror="this.style.display='none'" />` : ''}
        <h2>${isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName}</h2>
        <p>${companyInfo?.address ? companyInfo.address + ' | ' : ''}${companyInfo?.phone ? companyInfo.phone + ' | ' : ''}${companyInfo?.email || ''}</p>
      </div>
      <h1>${t.title}</h1>
      <table><thead><tr>
        <th>${t.saleNumber}</th><th>${t.branch}</th><th>${t.concept}</th><th>${t.date}</th>
        <th>${t.grossSales}</th><th>${t.discount}</th><th>${t.serviceCharge}</th><th>${t.netSales}</th>
        <th>${t.delivery}</th><th>${t.dineIn}</th><th>${t.takeAway}</th>
        ${payMethodsList.map(m => `<th>${m.length > 8 ? m.substring(0, 8) : m}</th>`).join('')}
        <th>${t.status}</th>
      </tr></thead><tbody>${rows}
      <tr class="total-row">
        <td colspan="4" style="text-align:${isRTL ? 'left' : 'right'};font-weight:bold">${isRTL ? 'الإجمالي' : 'Total'}</td>
        <td style="text-align:right">${totals.gross.toFixed(3)}</td>
        <td style="text-align:right">${totals.discount.toFixed(3)}</td>
        <td style="text-align:right">${totals.service.toFixed(3)}</td>
        <td style="text-align:right;font-weight:bold;color:#FF6F00">${totals.net.toFixed(3)}</td>
        <td style="text-align:right">${totals.delivery.toFixed(3)}</td>
        <td style="text-align:right">${totals.dineIn.toFixed(3)}</td>
        <td style="text-align:right">${totals.takeAway.toFixed(3)}</td>
        ${payMethodsList.map(m => {
          const methodTotal = filteredSales.reduce((sum, s) => {
            const pd = s.paymentDetails?.find(p => (isRTL ? (p.methodNameAr || p.methodName) : p.methodName) === m);
            return sum + (pd?.amount || 0);
          }, 0);
          return `<td style="text-align:right">${methodTotal.toFixed(3)}</td>`;
        }).join('')}
        <td style="text-align:center;font-weight:bold">${filteredSales.length}</td>
      </tr></tbody></table>
      <p style="margin-top:10px;font-size:10px;color:#999;">${isRTL ? 'تاريخ الطباعة:' : 'Printed:'} ${new Date().toLocaleString()}</p>
    </body></html>`;
    const w = window.open('', '_blank', 'width=1100,height=700');
    if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 500); }
  };
  const filteredSales = selectedConceptId ? (() => {
    const selectedConcept = concepts.find((c: any) => c.conceptId === selectedConceptId);
    const conceptName = selectedConcept?.conceptName || '';
    return sales.filter(s => s.conceptId === selectedConceptId || s.conceptName === conceptName);
  })() : sales;
  const paginatedSales = filteredSales.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const payMethods = getAllPaymentMethods();

  return (
    <Box sx={{ p: 3 }}>
      <style>{`
        @media print {
          @page { size: landscape; margin: 10mm; }
          body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
        }
      `}</style>
      <Card className="no-print" sx={{ mb: 3, bgcolor: '#FF6F00', color: 'white' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SalesIcon sx={{ fontSize: 32 }} />
              <Typography variant="h4" fontWeight="bold">{t.title}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Tooltip title={t.refresh}><IconButton sx={{ color: 'white' }} onClick={loadSales}><RefreshIcon /></IconButton></Tooltip>
              <Tooltip title={t.exportExcel}><IconButton sx={{ color: 'white' }} onClick={exportToExcel}><ExcelIcon /></IconButton></Tooltip>
              <Tooltip title={t.exportPDF}><IconButton sx={{ color: 'white' }} onClick={exportToPdf}><PdfIcon /></IconButton></Tooltip>
              <Tooltip title={t.print}><IconButton sx={{ color: 'white' }} onClick={handlePrint}><PrintIcon /></IconButton></Tooltip>
              <Button variant="contained" sx={{ bgcolor: 'white', color: '#FF6F00', '&:hover': { bgcolor: 'grey.100' } }} startIcon={<AddIcon />} onClick={handleAdd}>{t.addSale}</Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
      
      {/* Filter Bar */}
      <Card className="no-print" sx={{ mb: 2, background: (theme) => theme.palette.mode === 'dark' ? 'linear-gradient(135deg, #424242 0%, #616161 100%)' : 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)', border: '2px solid #FF6F00' }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <Box sx={{ flex: '1 1 250px', minWidth: '200px' }}>
              <Autocomplete
                size="small"
                options={branches}
                getOptionLabel={(o: any) => isRTL ? (o.branchNameAr || o.branchName) : o.branchName}
                value={branches.find((x: any) => x.branchId === selectedBranchId) || null}
                onChange={(_, v: any) => setSelectedBranchId(v?.branchId || null)}
                renderInput={(params) => <TextField {...params} label={t.filterByBranch}
                  sx={{
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? '#424242' : 'white',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#FF6F00' },
                  }} />}
                sx={{ minWidth: 200 }}
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
              <Autocomplete
                size="small"
                options={concepts}
                getOptionLabel={(o: any) => isRTL ? (o.conceptNameAr || o.conceptName) : o.conceptName}
                value={concepts.find((x: any) => x.conceptId === selectedConceptId) || null}
                onChange={(_, v: any) => { setSelectedConceptId(v?.conceptId || null); setPage(0); }}
                renderInput={(params) => <TextField {...params} label={t.filterByConcept}
                  sx={{
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? '#424242' : 'white',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#FF6F00' },
                  }} />}
                sx={{ minWidth: 180 }}
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={t.startDate}
                  value={startDate ? new Date(startDate) : null}
                  onChange={(date) => {
                    const newVal = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                    setStartDate(newVal);
                    if (endDate && newVal && endDate < newVal) setEndDate('');
                  }}
                  format="dd/MM/yyyy"
                  minDate={minDate}
                  maxDate={endDate ? new Date(endDate) : maxDate}
                  views={['year', 'month', 'day']}
                  openTo="day"
                  slotProps={{ textField: { fullWidth: true, size: 'small', sx: { bgcolor: (theme: any) => theme.palette.mode === 'dark' ? '#424242' : 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#FF6F00' }, '& .MuiInputBase-input': { color: (theme: any) => theme.palette.mode === 'dark' ? '#fff' : 'inherit' } } } }}
                />
              </LocalizationProvider>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={t.endDate}
                  value={endDate ? new Date(endDate) : null}
                  onChange={(date) => {
                    const newVal = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                    setEndDate(newVal);
                    if (startDate && newVal && startDate > newVal) setStartDate('');
                  }}
                  format="dd/MM/yyyy"
                  minDate={startDate ? new Date(startDate) : minDate}
                  maxDate={maxDate}
                  views={['year', 'month', 'day']}
                  openTo="day"
                  slotProps={{ textField: { fullWidth: true, size: 'small', sx: { bgcolor: (theme: any) => theme.palette.mode === 'dark' ? '#424242' : 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#FF6F00' }, '& .MuiInputBase-input': { color: (theme: any) => theme.palette.mode === 'dark' ? '#fff' : 'inherit' } } } }}
                />
              </LocalizationProvider>
            </Box>
            <Box sx={{ flex: '1 1 300px', minWidth: '250px', display: 'flex', gap: 1 }}>
              <Button
                fullWidth
                variant="contained"
                sx={{ bgcolor: '#FF6F00', color: '#fff', '&:hover': { bgcolor: '#E65100' } }}
                onClick={handleApplyFilters}
              >
                {t.applyFilters}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                sx={{ 
                  borderColor: '#FF6F00', 
                  color: (theme) => theme.palette.mode === 'dark' ? '#FFB74D' : '#FF6F00',
                  '&:hover': { borderColor: '#E65100', bgcolor: 'rgba(255, 111, 0, 0.08)' } 
                }}
                onClick={handleClearFilters}
              >
                {t.clearFilters}
              </Button>
            </Box>
          </Box>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Chip 
              icon={<SalesIcon sx={{ color: '#2E7D32 !important' }} />}
              label={`${t.totalRecords}: ${filteredSales.length}`}
              sx={{ 
                bgcolor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E8F5E9',
                color: (theme) => theme.palette.mode === 'dark' ? '#A5D6A7' : '#2E7D32',
                fontWeight: 'bold', 
                fontSize: '0.95rem', 
                px: 1 
              }}
            />
            <Chip 
              icon={<SalesIcon sx={{ color: '#E65100 !important' }} />}
              label={`${t.totalNet}: ${filteredSales.reduce((sum, s) => sum + (s.netSales || 0), 0).toFixed(3)} KD`}
              sx={{ 
                bgcolor: (theme) => theme.palette.mode === 'dark' ? '#BF360C' : '#FFF3E0',
                color: (theme) => theme.palette.mode === 'dark' ? '#FFAB91' : '#E65100',
                fontWeight: 'bold', 
                fontSize: '0.95rem', 
                px: 1 
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <Box ref={printRef}>
        <Box sx={{ display: 'none' }} className="print-only">
          <Box sx={{ textAlign: 'center', mb: 1, pb: 1, borderBottom: '2px solid #FF6F00' }}>
            {companyInfo?.logoBase64 && (<img src={companyInfo.logoBase64} alt="Logo" style={{ width: 60, height: 45, marginBottom: 6 }} />)}
            <Typography variant="h5" sx={{ color: '#FF6F00', fontWeight: 'bold', fontSize: '16px' }}>{isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName}</Typography>
            <Typography variant="body2" sx={{ fontSize: '8px', color: '#666' }}>{companyInfo?.address} | {companyInfo?.phone}</Typography>
          </Box>
          <Box sx={{ bgcolor: '#FF6F00', color: 'white', py: 0.3, px: 1, textAlign: 'center', mb: 0.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '12px' }}>{isRTL ? 'تقرير المبيعات اليومية' : 'Daily Sales Report'} - {new Date().toLocaleDateString(isRTL ? 'ar-EG' : 'en-GB')}</Typography>
          </Box>
        </Box>
        <TableContainer component={Paper}>
          {loading ? (<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}><CircularProgress sx={{ color: '#FF6F00' }} /></Box>) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#FF6F00' }}>
                    <TableCell className="no-print" sx={{ color: 'white', fontWeight: 'bold', width: 40, fontSize: '11px', p: 1 }} />
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '11px', p: 1 }}>{t.saleNumber}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '11px', p: 1 }}>{t.branch}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '11px', p: 1 }}>{t.concept}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '11px', p: 1 }}>{t.date}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '10px', p: 0.8 }} align="right">{t.grossSales}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '10px', p: 0.8 }} align="right">{t.discount}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '10px', p: 0.8 }} align="right">{t.serviceCharge}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '10px', p: 0.8 }} align="right">{t.netSales}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '9px', p: 0.6 }} align="right">{t.delivery}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '9px', p: 0.6 }} align="right">{t.dineIn}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '9px', p: 0.6 }} align="right">{t.takeAway}</TableCell>
                    {payMethods.map((method, idx) => (
                      <TableCell key={idx} sx={{ color: 'white', fontWeight: 'bold', fontSize: '9px', p: 0.5 }} align="right">{method.length > 7 ? method.substring(0, 7) : method}</TableCell>
                    ))}
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '10px', p: 0.8 }} align="center">{t.status}</TableCell>
                    <TableCell className="no-print" sx={{ color: 'white', fontWeight: 'bold', fontSize: '11px', p: 1 }} align="center">{t.actions}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedSales.map((sale) => {
                    const paymentMap: {[key: string]: number} = {};
                    sale.paymentDetails?.forEach(p => {
                      const methodName = isRTL ? (p.methodNameAr || p.methodName || '') : (p.methodName || '');
                      paymentMap[methodName] = (p.amount || 0);
                    });
                    return (
                      <React.Fragment key={sale.dailySaleId}>
                        <TableRow hover sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}>
                          <TableCell className="no-print" sx={{ p: 0.5 }}>
                            <IconButton size="small" onClick={() => setExpandedRow(expandedRow === sale.dailySaleId ? null : sale.dailySaleId)}>
                              <ExpandMoreIcon sx={{ transform: expandedRow === sale.dailySaleId ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.3s', fontSize: '18px' }} />
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ fontSize: '10px', p: 0.8 }}>{sale.saleNumber}</TableCell>
                          <TableCell sx={{ fontSize: '10px', p: 0.8 }}>{isRTL ? sale.branchNameAr : sale.branchName}</TableCell>
                          <TableCell sx={{ fontSize: '10px', p: 0.8 }}>{isRTL ? sale.conceptNameAr : sale.conceptName}</TableCell>
                          <TableCell sx={{ fontSize: '10px', p: 0.8 }}>{new Date(sale.saleDate).toLocaleDateString(isRTL ? 'ar-EG' : 'en-GB')}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '9px', p: 0.7 }}>{(sale.grossSales || 0).toFixed(3)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '9px', p: 0.7 }}>{(sale.discount || 0).toFixed(3)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '9px', p: 0.7 }}>{(sale.serviceCharge || 0).toFixed(3)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '9px', p: 0.7 }}>{(sale.netSales || 0).toFixed(3)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '8px', p: 0.5 }}>{(sale.deliveryAmount || 0).toFixed(3)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '8px', p: 0.5 }}>{(sale.dineInAmount || 0).toFixed(3)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '8px', p: 0.5 }}>{(sale.takeAwayAmount || 0).toFixed(3)}</TableCell>
                          {payMethods.map((method, idx) => (
                            <TableCell key={idx} align="right" sx={{ fontSize: '8px', p: 0.5 }}>{paymentMap[method] ? paymentMap[method].toFixed(3) : '0.000'}</TableCell>
                          ))}
                          <TableCell align="center" sx={{ p: 0.5 }}>{getStatusChip(sale)}</TableCell>
                          <TableCell className="no-print" align="center" sx={{ p: 0.5 }}>
                            <Tooltip title={t.print}><IconButton size="small" sx={{ color: '#1976d2' }} onClick={() => handlePrintSingle(sale)}><PrintIcon fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title={t.edit}><IconButton size="small" sx={{ color: '#FF6F00' }} onClick={() => handleEdit(sale)} disabled={sale.status !== 'Draft'}><EditIcon fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title={t.delete}><IconButton size="small" color="error" onClick={() => openDeleteDialog(sale.dailySaleId)} disabled={sale.status !== 'Draft'}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                            {sale.status === 'Draft' && (
                              <Tooltip title={t.submit}><IconButton size="small" sx={{ color: '#FF9800' }} onClick={() => openSubmitDialog(sale.dailySaleId)}><SendIcon fontSize="small" /></IconButton></Tooltip>
                            )}
                            {sale.status === 'Submitted' && !sale.isApproved && (
                              <>
                                <Tooltip title={t.approve}><IconButton size="small" sx={{ color: '#9C27B0' }} onClick={() => openApproveDialog(sale.dailySaleId)}><ApproveIcon fontSize="small" /></IconButton></Tooltip>
                                <Tooltip title={t.unsubmit}><IconButton size="small" sx={{ color: '#FF6F00' }} onClick={() => openUnsubmitDialog(sale.dailySaleId)}><RemoveIcon fontSize="small" /></IconButton></Tooltip>
                              </>
                            )}
                            {sale.isApproved && !sale.journalEntryId && (
                              <>
                                {canCreateEntry && <Tooltip title={t.createEntry}><IconButton size="small" sx={{ color: '#2196F3' }} onClick={() => openCreateEntryDialog(sale.dailySaleId)}><JournalIcon fontSize="small" /></IconButton></Tooltip>}
                                <Tooltip title={t.unapprove}><IconButton size="small" sx={{ color: '#FF6F00' }} onClick={() => openUnapproveDialog(sale.dailySaleId)}><RemoveIcon fontSize="small" /></IconButton></Tooltip>
                              </>
                            )}
                            {sale.journalEntryId && !sale.isPosted && (
                              <>
                                {canPost && <Tooltip title={t.post}><IconButton size="small" sx={{ color: '#4CAF50' }} onClick={() => openPostDialog(sale.dailySaleId)}><PostIcon fontSize="small" /></IconButton></Tooltip>}
                                {canDeleteEntry && <Tooltip title={t.deleteEntry}><IconButton size="small" sx={{ color: '#F44336' }} onClick={() => openDeleteEntryDialog(sale.dailySaleId)}><LayersClearIcon fontSize="small" /></IconButton></Tooltip>}
                              </>
                            )}
                            {sale.isPosted && canPost && (
                              <Tooltip title={t.unpost}><IconButton size="small" sx={{ color: '#F44336' }} onClick={() => openUnpostDialog(sale.dailySaleId)}><UnpostIcon fontSize="small" /></IconButton></Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={14 + payMethods.length}>
                            <Collapse in={expandedRow === sale.dailySaleId} timeout="auto" unmountOnExit>
                              <Box sx={{ margin: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ color: '#FF6F00' }}>{t.paymentDetails}</Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 'bold' }}>{t.paymentMethod}</TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t.amount}</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {sale.paymentDetails?.map((detail, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell>{isRTL ? detail.methodNameAr : detail.methodName}</TableCell>
                                        <TableCell align="right">{(detail.amount || 0).toFixed(3)} KD</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                  {paginatedSales.length === 0 && (<TableRow><TableCell colSpan={14 + payMethods.length} align="center" sx={{ py: 4 }}><Typography variant="body1" color="text.secondary">{t.noData}</Typography></TableCell></TableRow>)}
                </TableBody>
              </Table>
              <TablePagination className="no-print" rowsPerPageOptions={[10, 25, 50, 100]} component="div" count={filteredSales.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} labelRowsPerPage={t.rowsPerPage} />
            </>
          )}
        </TableContainer>
      </Box>

      {dialogOpen && (<DailySalesDialog open={dialogOpen} dailySale={selectedSale} onClose={handleDialogClose} />)}

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <Box sx={{ textAlign: 'center', pt: 3, pb: 2 }}>
          <WarningIcon sx={{ fontSize: 64, color: '#f44336', mb: 2 }} />
          <DialogTitle sx={{ p: 0, mb: 1, fontSize: '1.5rem', fontWeight: 'bold', color: '#f44336' }}>{t.delete}</DialogTitle>
        </Box>
        <DialogContent>
          <Typography align="center" sx={{ color: '#666', fontSize: '0.95rem' }}>{t.confirmDelete}</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 1 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined" sx={{ minWidth: 100 }}>{t.cancel}</Button>
          <Button onClick={handleDelete} variant="contained" sx={{ minWidth: 100, bgcolor: '#f44336', '&:hover': { bgcolor: '#d32f2f' } }}>{t.delete}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={submitDialogOpen} onClose={() => setSubmitDialogOpen(false)} maxWidth="xs" fullWidth>
        <Box sx={{ textAlign: 'center', pt: 3, pb: 2 }}>
          <SendIcon sx={{ fontSize: 64, color: '#FF9800', mb: 2 }} />
          <DialogTitle sx={{ p: 0, mb: 1, fontSize: '1.5rem', fontWeight: 'bold', color: '#FF9800' }}>{t.submit}</DialogTitle>
        </Box>
        <DialogContent>
          <Typography align="center" sx={{ color: '#666', fontSize: '0.95rem' }}>{t.confirmSubmit}</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 1 }}>
          <Button onClick={() => setSubmitDialogOpen(false)} variant="outlined" sx={{ minWidth: 100 }}>{t.cancel}</Button>
          <Button onClick={handleSubmit} variant="contained" sx={{ minWidth: 100, bgcolor: '#FF9800', '&:hover': { bgcolor: '#F57C00' } }}>{t.confirm}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={unsubmitDialogOpen} onClose={() => setUnsubmitDialogOpen(false)} maxWidth="xs" fullWidth>
        <Box sx={{ textAlign: 'center', pt: 3, pb: 2 }}>
          <RemoveIcon sx={{ fontSize: 64, color: '#FF6F00', mb: 2 }} />
          <DialogTitle sx={{ p: 0, mb: 1, fontSize: '1.5rem', fontWeight: 'bold', color: '#FF6F00' }}>{t.unsubmit}</DialogTitle>
        </Box>
        <DialogContent>
          <Typography align="center" sx={{ color: '#666', fontSize: '0.95rem' }}>{t.confirmUnsubmit}</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 1 }}>
          <Button onClick={() => setUnsubmitDialogOpen(false)} variant="outlined" sx={{ minWidth: 100 }}>{t.cancel}</Button>
          <Button onClick={handleUnsubmit} variant="contained" sx={{ minWidth: 100, bgcolor: '#FF6F00', '&:hover': { bgcolor: '#F57C00' } }}>{t.confirm}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="xs" fullWidth>
        <Box sx={{ textAlign: 'center', pt: 3, pb: 2 }}>
          <ApproveIcon sx={{ fontSize: 64, color: '#9C27B0', mb: 2 }} />
          <DialogTitle sx={{ p: 0, mb: 1, fontSize: '1.5rem', fontWeight: 'bold', color: '#9C27B0' }}>{t.approve}</DialogTitle>
        </Box>
        <DialogContent>
          <Typography align="center" sx={{ color: '#666', fontSize: '0.95rem' }}>{t.confirmApprove}</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 1 }}>
          <Button onClick={() => setApproveDialogOpen(false)} variant="outlined" sx={{ minWidth: 100 }}>{t.cancel}</Button>
          <Button onClick={handleApprove} variant="contained" sx={{ minWidth: 100, bgcolor: '#9C27B0', '&:hover': { bgcolor: '#7B1FA2' } }}>{t.confirm}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={unapproveDialogOpen} onClose={() => setUnapproveDialogOpen(false)} maxWidth="xs" fullWidth>
        <Box sx={{ textAlign: 'center', pt: 3, pb: 2 }}>
          <RemoveIcon sx={{ fontSize: 64, color: '#FF6F00', mb: 2 }} />
          <DialogTitle sx={{ p: 0, mb: 1, fontSize: '1.5rem', fontWeight: 'bold', color: '#FF6F00' }}>{t.unapprove}</DialogTitle>
        </Box>
        <DialogContent>
          <Typography align="center" sx={{ color: '#666', fontSize: '0.95rem' }}>{t.confirmUnapprove}</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 1 }}>
          <Button onClick={() => setUnapproveDialogOpen(false)} variant="outlined" sx={{ minWidth: 100 }}>{t.cancel}</Button>
          <Button onClick={handleUnapprove} variant="contained" sx={{ minWidth: 100, bgcolor: '#FF6F00', '&:hover': { bgcolor: '#F57C00' } }}>{t.confirm}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createEntryDialogOpen} onClose={() => setCreateEntryDialogOpen(false)} maxWidth="xs" fullWidth>
        <Box sx={{ textAlign: 'center', pt: 3, pb: 2 }}>
          <JournalIcon sx={{ fontSize: 64, color: '#2196F3', mb: 2 }} />
          <DialogTitle sx={{ p: 0, mb: 1, fontSize: '1.5rem', fontWeight: 'bold', color: '#2196F3' }}>{t.createEntry}</DialogTitle>
        </Box>
        <DialogContent>
          <Typography align="center" sx={{ color: '#666', fontSize: '0.95rem' }}>{t.confirmCreateEntry}</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 1 }}>
          <Button onClick={() => setCreateEntryDialogOpen(false)} variant="outlined" sx={{ minWidth: 100 }}>{t.cancel}</Button>
          <Button onClick={handleCreateEntry} variant="contained" sx={{ minWidth: 100, bgcolor: '#2196F3', '&:hover': { bgcolor: '#1976D2' } }}>{t.confirm}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteEntryDialogOpen} onClose={() => setDeleteEntryDialogOpen(false)} maxWidth="xs" fullWidth>
        <Box sx={{ textAlign: 'center', pt: 3, pb: 2 }}>
          <WarningIcon sx={{ fontSize: 64, color: '#F44336', mb: 2 }} />
          <DialogTitle sx={{ p: 0, mb: 1, fontSize: '1.5rem', fontWeight: 'bold', color: '#F44336' }}>{t.deleteEntry}</DialogTitle>
        </Box>
        <DialogContent>
          <Typography align="center" sx={{ color: '#666', fontSize: '0.95rem' }}>{t.confirmDeleteEntry}</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 1 }}>
          <Button onClick={() => setDeleteEntryDialogOpen(false)} variant="outlined" sx={{ minWidth: 100 }}>{t.cancel}</Button>
          <Button onClick={handleDeleteEntry} variant="contained" sx={{ minWidth: 100, bgcolor: '#F44336', '&:hover': { bgcolor: '#D32F2F' } }}>{t.confirm}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={postDialogOpen} onClose={() => setPostDialogOpen(false)} maxWidth="xs" fullWidth>
        <Box sx={{ textAlign: 'center', pt: 3, pb: 2 }}>
          <PostIcon sx={{ fontSize: 64, color: '#4CAF50', mb: 2 }} />
          <DialogTitle sx={{ p: 0, mb: 1, fontSize: '1.5rem', fontWeight: 'bold', color: '#4CAF50' }}>{t.post}</DialogTitle>
        </Box>
        <DialogContent>
          <Typography align="center" sx={{ color: '#666', fontSize: '0.95rem' }}>{t.confirmPost}</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 1 }}>
          <Button onClick={() => setPostDialogOpen(false)} variant="outlined" sx={{ minWidth: 100 }}>{t.cancel}</Button>
          <Button onClick={handlePost} variant="contained" sx={{ minWidth: 100, bgcolor: '#4CAF50', '&:hover': { bgcolor: '#388E3C' } }}>{t.confirm}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={unpostDialogOpen} onClose={() => setUnpostDialogOpen(false)} maxWidth="xs" fullWidth>
        <Box sx={{ textAlign: 'center', pt: 3, pb: 2 }}>
          <UnpostIcon sx={{ fontSize: 64, color: '#F44336', mb: 2 }} />
          <DialogTitle sx={{ p: 0, mb: 1, fontSize: '1.5rem', fontWeight: 'bold', color: '#F44336' }}>{t.unpost}</DialogTitle>
        </Box>
        <DialogContent>
          <Typography align="center" sx={{ color: '#666', fontSize: '0.95rem' }}>{t.confirmUnpost}</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 1 }}>
          <Button onClick={() => setUnpostDialogOpen(false)} variant="outlined" sx={{ minWidth: 100 }}>{t.cancel}</Button>
          <Button onClick={handleUnpost} variant="contained" sx={{ minWidth: 100, bgcolor: '#F44336', '&:hover': { bgcolor: '#D32F2F' } }}>{t.confirm}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DailySalesPage;